import CopyIcon from '@mui/icons-material/ContentCopy';
import { Button, FormControlLabel, InputLabel, Radio, RadioGroup, Slider, Typography } from '@mui/material';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCodeSimple } from 'react-icons/pi';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookMessengerIcon,
  FacebookMessengerShareButton,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  PinterestIcon,
  PinterestShareButton,
  RedditIcon,
  RedditShareButton,
  TumblrIcon,
  TumblrShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon
} from 'react-share';
import './share.scss';

interface ShareModalProps {
  shareUrl: string;
  onClose: () => void;
}

interface EmbedCodeModalProps {
  t: (key: string) => string;
  embedZoom: number;
  embedPinSize: string;
  embedCode: string;
  embedCodeInputRef: React.RefObject<HTMLInputElement | null>;
  makeIFrameURL: (zoom?: string, pinsize?: string) => string;
  toggleEmbedCode: () => void;
  handleZoomChange: (_e: Event, newValue: number | number[]) => void;
  handlePinSizeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  copyEmbedCodeToClipboard: () => void;
}

const EmbedCodeModal = ({
  t,
  embedZoom,
  embedPinSize,
  embedCode,
  embedCodeInputRef,
  makeIFrameURL,
  toggleEmbedCode,
  handleZoomChange,
  handlePinSizeChange,
  copyEmbedCodeToClipboard
}: EmbedCodeModalProps) => {
  return (
    <div className='embed-code-modal'>
      <div className='embed-code-content'>
        <span className='close-btn' onClick={toggleEmbedCode}>
          X
        </span>
        <p className='title2'>{t('share.embed')}</p>

        <InputLabel htmlFor='zoom-slider'>{t('share.customizeMapZoom')}</InputLabel>
        <Slider
          id='zoom-slider'
          aria-label='Zoom'
          defaultValue={8}
          value={embedZoom}
          valueLabelDisplay='auto'
          step={1}
          marks
          min={1}
          max={15}
          onChange={handleZoomChange}
        />
        <InputLabel id='pin-size-radio-group-label'>{t('share.customizeIconSize')}</InputLabel>
        <RadioGroup
          aria-labelledby='pin-size-radio-group-label'
          defaultValue='default'
          name='radio-buttons-group'
          value={embedPinSize}
          onChange={handlePinSizeChange}
        >
          <FormControlLabel
            key='radio-xs'
            value='xs'
            control={<Radio />}
            label={<Typography sx={{ color: 'black' }}>XS</Typography>}
          />
          <FormControlLabel
            key='radio-s'
            value='s'
            control={<Radio />}
            label={<Typography sx={{ color: 'black' }}>S</Typography>}
          />
          <FormControlLabel
            key='radio-default'
            value='default'
            control={<Radio />}
            label={<Typography sx={{ color: 'black' }}>{t('share.standard')}</Typography>}
          />
        </RadioGroup>
        <input
          ref={embedCodeInputRef}
          type='text'
          value={embedCode}
          readOnly
          onClick={() => embedCodeInputRef.current?.select()}
        />
        <Button onClick={copyEmbedCodeToClipboard} startIcon={<CopyIcon />}>
          {t('share.copyCode')}
        </Button>
        <Typography
          sx={{
            color: '#777777',
            fontSize: '12pt'
          }}
        >
          {t('share.embedPreview')}
        </Typography>
        <iframe
          title='preview-iframe'
          src={makeIFrameURL(embedZoom.toString(), embedPinSize)}
          width='100%'
          height='400'
          className='preview-iframe'
        ></iframe>
      </div>
    </div>
  );
};

const ShareModal: React.FC<ShareModalProps> = ({ shareUrl, onClose }) => {
  const { t } = useTranslation();
  const [scrollPosition, setScrollPosition] = useState(0);
  const contentWidth = 100; // Width of each item
  const [visibleItems, setVisibleItems] = useState(4); // Number of visible items
  const containerRef = useRef<HTMLDivElement>(null);

  const [itemSize, setItemSize] = useState(70);
  const [itemSizeEmbed, setItemSizeEmbed] = useState(70);

  const [embedCode, setEmbedCode] = useState('');
  const [embedCodeVisible, setEmbedCodeVisible] = useState(false);
  const embedCodeInputRef = useRef<HTMLInputElement | null>(null);

  const [embedZoom, setEmbedZoom] = useState<number>(7);
  const [embedPinSize, setEmbedPinSize] = useState<string>('default');
  const [embedCenter, setEmbedCenter] = useState<{ lat: number; lng: number } | null>(null);
  const lastReceivedCenterRef = useRef<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    const handleResize = () => {
      // Calculate item size based on the window width or any specific logic
      const newSize = Math.min(Math.floor(window.innerWidth * 0.1), 70);
      const newSizeEmbed = Math.min(Math.floor(window.innerWidth * 0.1), 70);
      setItemSize(newSize);
      setItemSizeEmbed(newSizeEmbed);
    };

    handleResize(); // Set initial size

    window.addEventListener('resize', handleResize); // Update size on window resize

    return () => {
      window.removeEventListener('resize', handleResize); // Cleanup on component unmount
    };
  }, []);

  // Calculate number of visible items based on screen width
  useEffect(() => {
    const handleResizeItems = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 600) {
        setVisibleItems(2);
      } else if (screenWidth >= 600 && screenWidth < 900) {
        setVisibleItems(3);
      } else {
        setVisibleItems(4);
      }
    };

    handleResizeItems();
    window.addEventListener('resize', handleResizeItems);

    return () => {
      window.removeEventListener('resize', handleResizeItems);
    };
  }, []);

  const items = [
    <div className='item'>
      <FacebookShareButton url={shareUrl}>
        <FacebookIcon size={itemSize} round />
        <div className='name'>Facebook</div>
      </FacebookShareButton>
    </div>,

    <div className='item'>
      <FacebookMessengerShareButton url={shareUrl} appId='521270401588372' className='Demo__some-network__share-button'>
        <FacebookMessengerIcon size={itemSize} round />
        <div className='name'>Messenger</div>
      </FacebookMessengerShareButton>
    </div>,

    <div className='item'>
      <TwitterShareButton url={shareUrl} className='Demo__some-network__share-button'>
        <XIcon size={itemSize} round />
        <div className='name'>Twitter</div>
      </TwitterShareButton>
    </div>,

    <div className='item'>
      <WhatsappShareButton url={shareUrl} separator=':: ' className='Demo__some-network__share-button'>
        <WhatsappIcon size={itemSize} round />
        <div className='name'>WhatsApp</div>
      </WhatsappShareButton>
    </div>,

    <div className='item'>
      <LinkedinShareButton url={shareUrl} className='Demo__some-network__share-button'>
        <LinkedinIcon size={itemSize} round />
        <div className='name'>LinkedIn</div>
      </LinkedinShareButton>
    </div>,

    <div className='item'>
      <PinterestShareButton
        url={String(window.location)}
        media={`${String(window.location)}/`}
        className='Demo__some-network__share-button'
      >
        <PinterestIcon size={itemSize} round />
        <div className='name'>Pinterest</div>
      </PinterestShareButton>
    </div>,

    <div className='item'>
      <RedditShareButton
        url={shareUrl}
        windowWidth={660}
        windowHeight={460}
        className='Demo__some-network__share-button'
      >
        <RedditIcon size={itemSize} round />
        <div className='name'>Reddit</div>
      </RedditShareButton>
    </div>,

    <div className='item'>
      <TumblrShareButton url={shareUrl} className='Demo__some-network__share-button'>
        <TumblrIcon size={itemSize} round />
        <div className='name'>Tumblr</div>
      </TumblrShareButton>
    </div>,

    <div className='item'>
      <EmailShareButton url={shareUrl} body='body' className='Demo__some-network__share-button'>
        <EmailIcon size={itemSize} round />
        <div className='name'>Email</div>
      </EmailShareButton>
    </div>
  ];

  const handleScroll = (direction: string) => {
    const container = containerRef.current;
    if (container) {
      const scrollAmount = container.offsetWidth / visibleItems;
      const maxScroll = container.scrollWidth - container.clientWidth;

      if (direction === 'left') {
        setScrollPosition(Math.max(scrollPosition - scrollAmount, 0));
      } else {
        setScrollPosition(Math.min(scrollPosition + scrollAmount, maxScroll));
      }
    }
  };

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
  };

  const makeIFrameURL = useCallback(
    (zoom?: string, pinsize?: string, center?: { lat: number; lng: number } | null) => {
      const urlObj = new URL(shareUrl);
      const searchParams = urlObj.searchParams;
      searchParams.set('zoom', zoom ?? '7');
      searchParams.set('pinsize', pinsize ?? 'default');
      if (center && Number.isFinite(center.lat) && Number.isFinite(center.lng)) {
        searchParams.set('center', `${center.lat},${center.lng}`);
      }
      const filteredParams = searchParams.toString();
      const queryString = filteredParams ? `?${filteredParams}` : '';
      return urlObj.origin + urlObj.pathname + queryString;
    },
    [shareUrl]
  );

  useEffect(() => {
    const generatedEmbedCode = `<iframe src="${makeIFrameURL(
      embedZoom.toString(),
      embedPinSize,
      embedCenter
    )}" width="600" height="400" frameborder="0" scrolling="no"></iframe>`;
    setEmbedCode(generatedEmbedCode);
  }, [shareUrl, embedPinSize, embedZoom, embedCenter, makeIFrameURL]);

  const toggleEmbedCode = () => {
    if (embedCodeVisible) {
      setEmbedCenter(null);
      lastReceivedCenterRef.current = null;
    }
    setEmbedCodeVisible(!embedCodeVisible);
  };

  // Listen for map center from preview iframe so we can inject center= into the embed URL
  useEffect(() => {
    if (!embedCodeVisible) return;
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (
        data?.type === 'map-center' &&
        data?.center &&
        typeof data.center.lat === 'number' &&
        typeof data.center.lng === 'number'
      ) {
        const next = { lat: data.center.lat, lng: data.center.lng };
        const prev = lastReceivedCenterRef.current;
        if (prev && Math.abs(prev.lat - next.lat) < 1e-7 && Math.abs(prev.lng - next.lng) < 1e-7) return;
        lastReceivedCenterRef.current = next;
        setEmbedCenter(next);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [embedCodeVisible]);

  const copyEmbedCodeToClipboard = () => {
    if (embedCodeInputRef.current) {
      embedCodeInputRef.current.select();
      document.execCommand('copy');
    }
  };

  const handleZoomChange = (_e: Event, newValue: number | number[]) => {
    if (typeof newValue == 'number') setEmbedZoom(newValue);
  };

  const handlePinSizeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmbedPinSize(e.target.value);
  };

  return (
    <div className='modal-overlay'>
      <div className='modal'>
        <div className='modal-header'>
          <div className='modal-title'>{t('share.share')}</div>
          <button className='close-btn' onClick={onClose}>
            X
          </button>
        </div>
        <div className='modal-content'>
          <div className='share-buttons-container'>
            <div className='scroll-container'>
              <div className='btn-background'>
                <button className='scroll-buttons' onClick={() => handleScroll('left')} disabled={scrollPosition === 0}>
                  {'<'}
                </button>
              </div>
              <div className='content-wrapper' ref={containerRef}>
                <div
                  className='content'
                  style={{
                    transform: `translateX(-${scrollPosition}px)`,
                    width: `${contentWidth * items.length + 100}px`
                  }}
                >
                  <div className='icon2'>
                    <div className='icon' style={{ width: `${itemSize}px`, height: `${itemSize}px` }}>
                      <PiCodeSimple size={itemSizeEmbed - 20} onClick={toggleEmbedCode} />
                    </div>
                    <div className='name'>Embed</div>
                  </div>
                  {items.map((item, index) => (
                    <div key={index} className='item' style={{ width: `${contentWidth}px` }}>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              <div className='btn-background'>
                <button
                  className='scroll-buttons'
                  onClick={() => handleScroll('right')}
                  disabled={
                    !containerRef.current ||
                    scrollPosition >=
                      (containerRef.current?.scrollWidth ?? 0) - (containerRef.current?.offsetWidth ?? 0)
                  }
                >
                  {'>'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className='modal-footer'>
          <div className='link-container'>
            <div className='page-link'>
              <span>{shareUrl}</span>
              <button className='copy-button' onClick={copyLinkToClipboard}>
                {t('share.copy')}
              </button>
            </div>
            <div className={`overlay ${embedCodeVisible ? 'active' : ''}`} />
          </div>
        </div>
      </div>
      {embedCodeVisible && (
        <EmbedCodeModal
          t={t}
          embedZoom={embedZoom}
          embedPinSize={embedPinSize}
          embedCode={embedCode}
          embedCodeInputRef={embedCodeInputRef}
          makeIFrameURL={makeIFrameURL}
          toggleEmbedCode={toggleEmbedCode}
          handleZoomChange={handleZoomChange}
          handlePinSizeChange={handlePinSizeChange}
          copyEmbedCodeToClipboard={copyEmbedCodeToClipboard}
        />
      )}
    </div>
  );
};
export default ShareModal;
